import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  EditNote as EditNoteIcon,
  PauseCircleFilled as PauseCircleFilledIcon,
  People as CustomersIcon,
  RocketLaunch as RocketLaunchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Switch,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  AdminEmptyState,
  AdminDialog,
  PageShell,
  AdminTableActions,
  AdminTableLoadingRow,
  AdminTablePanel,
} from "@/components/common";
import { LocalizedTablePagination } from "@/components/common/LocalizedTablePagination";
import { LoadingRing } from "@/components/common/LoadingRing";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import { CustomerEditDialog } from "@/pages/admin/organization/customers/CustomerEditDialog";

interface CustomerFormData {
  name: string;
  initial: string;
  saveForLaterTemplate: string;
  live: boolean;
  active: boolean;
}
type CustomerSortColumn = "id" | "initial" | "name" | "active" | "live";

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const customerService = CustomerService.getInstance();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<CustomerFormData>({
    name: "",
    initial: "",
    saveForLaterTemplate: "",
    live: false,
    active: true,
  });
  const visibleCustomers = showInactive
    ? customers
    : customers.filter((customer) => customer.active);
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    visibleCustomers,
    (customer, column) =>
      ({
        id: customer.id,
        initial: customer.initial,
        name: customer.name,
        active: customer.active,
        live: customer.live,
      })[column],
    "id" as CustomerSortColumn,
  );

  useEffect(() => {
    document.title = t("management.customers.customers.documentTitle");
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await customerService.getAllCustomers({ includeInactive: true });

      if (response.success && response.data) {
        setCustomers(response.data);
      } else {
        setError(response.error || t("management.customers.customers.errors.failedFetchCustomers"));
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t("management.customers.customers.errors.unexpectedFetchCustomers"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setDialogError(null);
    const hasNameError = !formData.name.trim();
    setNameError(hasNameError);
    if (hasNameError) {
      nameInputRef.current?.focus();
      return;
    }
    setIsLoading(true);

    try {
      const response = await customerService.addCustomer(formData);
      if (response.success && response.data) {
        setCustomers((prev) => [...prev, response.data as Customer]);
        setIsModalOpen(false);
        resetForm();
      } else {
        setDialogError(
          response.error || t("management.customers.customers.errors.failedCreateCustomer"),
        );
      }
    } catch (err) {
      setDialogError(
        err instanceof Error
          ? err.message
          : t("management.customers.customers.errors.errorCreatingCustomer"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      initial: "",
      saveForLaterTemplate: "",
      live: false,
      active: true,
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "radio" ? value === "true" : value,
    }));
    if (name === "name" && value.trim()) setNameError(false);
  };

  const closeAddDialog = () => {
    setIsModalOpen(false);
    setDialogError(null);
    setNameError(false);
  };

  const navigateToNotes = (customerId: number) => {
    navigate(`/admin/organization/customers/notes/${customerId}`);
  };

  const navigateToConfig = (customerId: number) => {
    void navigate(`/admin/configuration/configs/edit/${customerId}`);
  };

  const renderActiveIcon = (customer: Customer) => (
    <Tooltip
      title={
        customer.active
          ? t("management.customers.customers.fields.active")
          : t("commandCenter.status.inactive")
      }
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        {customer.active ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
        ) : (
          <CancelIcon color="error" sx={{ fontSize: 18 }} />
        )}
      </Box>
    </Tooltip>
  );

  const renderLiveIcon = (customer: Customer) => (
    <Tooltip
      title={
        customer.live
          ? t("management.customers.customers.fields.live")
          : t("management.customers.customers.fields.nonLive")
      }
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        {customer.live ? (
          <RocketLaunchIcon color="success" sx={{ fontSize: 18 }} />
        ) : (
          <PauseCircleFilledIcon color="disabled" sx={{ fontSize: 18 }} />
        )}
      </Box>
    </Tooltip>
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1));
  const displayedCustomers = sortedItems.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );
  const handleCustomerSort = (column: CustomerSortColumn) => {
    setPage(0);
    handleSort(column);
  };
  const renderPagination = () => (
    <LocalizedTablePagination
      component="div"
      count={sortedItems.length}
      page={currentPage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, next) => setPage(next)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
    />
  );

  const renderDesktopView = () => (
    <AdminTablePanel
      footer={renderPagination()}
      header={
        <Box>
          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(event) => {
                    setShowInactive(event.target.checked);
                    setPage(0);
                  }}
                />
              }
              label={t("management.customers.customers.actions.showInactive")}
            />
          </Box>
          {renderPagination()}
        </Box>
      }

      mobileContent={
        <Box
          sx={{
            minWidth: 0,
            "& .MuiTypography-root": {
              maxWidth: "100%",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            },
          }}
        >
          <RecordSortControl
            value={sortColumn}
            direction={sortDirection}
            onSort={handleCustomerSort}
            options={[
              { value: "id" as const, label: t("management.customers.customers.fields.id") },
              { value: "live" as const, label: t("management.customers.customers.fields.live") },
              {
                value: "initial" as const,
                label: t("management.customers.customers.fields.initial"),
              },
              {
                value: "name" as const,
                label: t("management.customers.customers.fields.customerName"),
              },
              ...(showInactive
                ? [
                    {
                      value: "active" as const,
                      label: t("management.customers.customers.fields.active"),
                    },
                  ]
                : []),
            ]}
          />
          <RecordList
            items={displayedCustomers}
            getItemId={(customer) => customer.id}
            loading={isLoading}
            resetKey={JSON.stringify([
              sortColumn,
              sortDirection,
              ...displayedCustomers.map((customer) => customer.id),
            ])}
            renderTitle={(customer) => {
              return <>{customer.name}</>;
            }}
            renderSummary={(customer) => {
              return (
                <RecordFields
                  fields={[
                    {
                      label: <>{t("management.customers.customers.fields.live")}</>,
                      value: <>{renderLiveIcon(customer)}</>,
                    },
                    {
                      label: <>{t("management.customers.customers.fields.initial")}</>,
                      value: <>{customer.initial ?? "—"}</>,
                    },
                  ]}
                />
              );
            }}
            renderDetails={(customer) => {
              return (
                <RecordFields
                  fields={[
                    {
                      label: <>{t("management.customers.customers.fields.id")}</>,
                      value: <>{customer.id}</>,
                    },
                    ...(showInactive
                      ? [
                          {
                            label: <>{t("management.customers.customers.fields.active")}</>,
                            value: <>{renderActiveIcon(customer)}</>,
                          },
                        ]
                      : []),
                  ]}
                />
              );
            }}
            renderActions={(customer) => (
              <AdminTableActions
                actions={[
                  {
                    icon: <EditNoteIcon />,
                    hint: t("management.customers.customers.actions.editNotes"),
                    action: () => navigateToNotes(customer.id),
                  },
                  {
                    icon: <SettingsIcon />,
                    hint: t("operations.configs.configs.tooltips.editConfiguration"),
                    action: () => navigateToConfig(customer.id),
                  },
                  {
                    icon: <EditIcon />,
                    hint: t("management.customers.customers.actions.edit"),
                    action: () => setEditingCustomer(customer),
                  },
                ]}
              />
            )}

            emptyState={
              <AdminEmptyState
                icon={<CustomersIcon sx={{ fontSize: 40 }} />}
                text={t("common.noDataAvailable")}
              />
            }
          />
        </Box>
      }
    >
      <TableHead>
        <TableRow>
          <SortableTableCell
            activeColumn={sortColumn}
            column="id"
            direction={sortDirection}
            onSort={handleCustomerSort}
            sx={{ width: "1%", whiteSpace: "nowrap" }}
          >
            {t("management.customers.customers.fields.id")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="live"
            direction={sortDirection}
            onSort={handleCustomerSort}
            sx={{
              width: "1%",
              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          >
            {t("management.customers.customers.fields.live")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="initial"
            direction={sortDirection}
            onSort={handleCustomerSort}
            sx={{ width: 100, whiteSpace: "nowrap", textAlign: "center" }}
          >
            {t("management.customers.customers.fields.initial")}
          </SortableTableCell>
          <SortableTableCell
            activeColumn={sortColumn}
            column="name"
            direction={sortDirection}
            onSort={handleCustomerSort}
          >
            {t("management.customers.customers.fields.customerName")}
          </SortableTableCell>
          {showInactive ? (
            <SortableTableCell
              activeColumn={sortColumn}
              column="active"
              direction={sortDirection}
              onSort={handleCustomerSort}
              sx={{
                width: 100,
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {t("management.customers.customers.fields.active")}
            </SortableTableCell>
          ) : null}
          <TableCell sx={{ width: "1%", whiteSpace: "nowrap", textAlign: "end" }}>
            {t("management.customers.customers.fields.actions")}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <AdminTableLoadingRow colSpan={showInactive ? 6 : 5} label={t("common.loading")} />
        ) : sortedItems.length > 0 ? (
          displayedCustomers.map((customer) => (
            <TableRow key={customer.id}>
              <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>{customer.id}</TableCell>
              <TableCell sx={{ width: "1%", textAlign: "center", whiteSpace: "nowrap" }}>
                {renderLiveIcon(customer)}
              </TableCell>
              <TableCell sx={{ width: 100, textAlign: "center" }}>
                {customer.initial ?? "—"}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{customer.name}</TableCell>
              {showInactive && (
                <TableCell sx={{ width: 100, textAlign: "center" }}>
                  {renderActiveIcon(customer)}
                </TableCell>
              )}
              <TableCell sx={{ width: "1%", whiteSpace: "nowrap", textAlign: "end" }}>
                <AdminTableActions
                  actions={[
                    {
                      icon: <EditNoteIcon />,
                      hint: t("management.customers.customers.actions.editNotes"),
                      action: () => navigateToNotes(customer.id),
                    },
                    {
                      icon: <SettingsIcon />,
                      hint: t("operations.configs.configs.tooltips.editConfiguration"),
                      action: () => navigateToConfig(customer.id),
                    },
                    {
                      icon: <EditIcon />,
                      hint: t("management.customers.customers.actions.edit"),
                      action: () => setEditingCustomer(customer),
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={showInactive ? 6 : 5} align="center">
              <AdminEmptyState
                icon={<CustomersIcon sx={{ fontSize: 40 }} />}
                text={t("common.noDataAvailable")}
              />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </AdminTablePanel>
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.customers.customers.title")}
      icon={<CustomersIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
      headerActions={
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setDialogError(null);
            setNameError(false);
            setIsModalOpen(true);
          }}
        >
          {t("management.customers.customers.actions.addCustomer")}
        </Button>
      }
    >
      <Box sx={{ mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderDesktopView()}
      </Box>

      <AdminDialog
        open={isModalOpen}
        onClose={closeAddDialog}
        maxWidth="sm"
        title={t("management.customers.customers.dialogs.addCustomerTitle")}
        icon={<CustomersIcon />}
        error={dialogError}
        errorOperation="create"
        errorEntity={t("common.entities.customer")}
        cancelAction={{ label: t("common.cancel"), onClick: closeAddDialog }}
        primaryAction={{
          label: t("management.customers.customers.actions.add"),
          onClick: () => void handleSubmit(),
          loading: isLoading,
        }}
      >
        <form noValidate onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t("management.customers.customers.fields.customerName")}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            inputRef={nameInputRef}
            error={nameError}
            helperText={nameError ? t("common.required") : undefined}
            required
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("management.customers.customers.fields.initial")}
            name="initial"
            value={formData.initial}
            onChange={handleInputChange}
            inputProps={{ maxLength: 10 }}
            margin="normal"
          />
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.live}
                    onChange={(event) =>
                      setFormData((current) => ({ ...current, live: event.target.checked }))
                    }
                  />
                }
                label={t("management.customers.customers.fields.live")}
              />
              <Typography variant="body2" color="text.secondary">
                {t("management.customers.customers.hints.live")}
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(event) =>
                      setFormData((current) => ({ ...current, active: event.target.checked }))
                    }
                  />
                }
                label={t("management.customers.customers.fields.active")}
              />
              <Typography variant="body2" color="text.secondary">
                {t("management.customers.customers.hints.active")}
              </Typography>
            </Box>
          </Box>
        </form>
      </AdminDialog>
      <CustomerEditDialog
        customer={editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSuccess={(updatedCustomer) =>
          setCustomers((current) =>
            current.map((customer) =>
              customer.id === updatedCustomer.id ? updatedCustomer : customer,
            ),
          )
        }
      />
    </PageShell>
  );
};

export default Customers;
